import siteOverviewStyles from './site-overview.styles.css?inline';
import '../../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles as sharedStyles } from '../../../styles/shared-styles';
import type {
  LoginSite,
  LoginSiteDomainScopeGrant,
  Address,
} from '../../../api/agwa-api-model';
import { terms } from '../../../language/terms';
import {
  normalizePhoneNumber,
  phoneLinkTarget,
} from '../../../utility/phone-number';
import type { SiteWorkspaceStore } from './site-workspace-store';
import { router, resolveRouterPath } from '../../../router';
import {
  MEMBER_USER_SEARCH_TAG,
  MEMBER_USER_SEARCH_CONTEXT,
} from '../../../components/search-picker/member-user-search-context';
import type { SearchPickerSelection } from '../../../components/search-picker/agwa-search-picker';
import type { SearchDialogSelection } from '../../../components/search-picker/agwa-search-dialog';

import '../../../components/address/agwa-address.js';
import '../../../components/address/agwa-site-address-edit.js';
import '../../../components/search-picker/agwa-search-picker.js';
import '../../../components/search-picker/agwa-search-dialog.js';
import '@shoelace-style/shoelace/dist/components/input/input.js';
import '@shoelace-style/shoelace/dist/components/textarea/textarea.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/skeleton/skeleton.js';
import '@shoelace-style/shoelace/dist/components/switch/switch.js';
import '@shoelace-style/shoelace/dist/components/divider/divider.js';
import '@shoelace-style/shoelace/dist/components/alert/alert.js';
import './site-operation-types-section.js';
import '../../../components/user/username.js';

/** What Change Contact needs of the site's edit lock (the workspace's RecordEditLock). */
export interface SiteEditLock {
  take(): Promise<boolean>;
  release(): Promise<void>;
}

@customElement('site-overview')
export class SiteOverview extends LitElement {
  static styles = [sharedStyles, unsafeCSS(siteOverviewStyles)];

  @property({ attribute: false }) site: LoginSite | null = null;
  @property({ attribute: false }) mapImageUrl: string | null = null;
  // Owner contact, resolved server-side and threaded from the store. Null when the site has no owner;
  // the phone is also null when the owner has none (D-151 rule 4).
  @property({ attribute: false }) ownerName: string | null = null;
  @property({ attribute: false }) ownerEmail: string | null = null;
  @property({ attribute: false }) ownerPhoneNumber: string | null = null;
  @property({ attribute: false }) domains: LoginSiteDomainScopeGrant[] = [];
  @property({ type: Boolean }) loading = false;
  @property({ type: Boolean }) isNew = false;
  @property({ type: String }) groupId = '';
  @property({ attribute: false }) store: SiteWorkspaceStore | null = null;
  /**
   * The site's edit lock, which the workspace holds (#398 C13a, D-163). Change Contact takes it for
   * the change and releases it after (@LOCK-045); null for a site not yet created.
   */
  @property({ attribute: false }) editLock: SiteEditLock | null = null;
  /**
   * Another person is editing this site: Change Contact is withdrawn while they are (@LOCK-040),
   * since the change would be refused, and the workspace's banner names them.
   */
  @property({ type: Boolean }) lockedByOther = false;

  @state() private editing = false;
  @state() private saving = false;
  @state() private editData: Partial<LoginSite> = {};
  @state() private validationError = '';

  // Standalone "Change Contact" flow (read-only mode): a host-controlled search dialog whose selection
  // is committed immediately via the store's setOwner (the PUT {id}/owner endpoint), independent of
  // the scalar edit form. _ownerActionError surfaces a failed owner change.
  @state() private ownerDialogOpen = false;
  @state() private ownerActionBusy = false;
  @state() private ownerActionError = '';
  /** This overview took the site's lock for an open Change Contact, and owes its release. */
  private contactLockHeld = false;

  updated(changedProps: Map<string, unknown>) {
    // Auto-enter edit mode for new sites
    if (changedProps.has('isNew') && this.isNew && !this.editing) {
      this.startEdit();
    }
  }

  /**
   * The workspace ACTION BAR drives editing since 2026-09-05, matching the company and
   * product pages; editing-changed keeps the bar's buttons in step with the state here.
   * New-site mode auto-enters editing, so the bar shows Save/Cancel for creation too.
   */
  private emitEditState() {
    this.dispatchEvent(
      new CustomEvent('editing-changed', {
        detail: { editing: this.editing, saving: this.saving },
        bubbles: true,
        composed: true,
      })
    );
  }

  startEdit() {
    if (!this.site) return;
    this.editData = {
      name: this.site.name,
      description: this.site.description,
      // Seed the owner from the site so the form always carries the intended final owner. Owner
      // rides the save DTO (final-state: this value becomes the owner, undefined clears it), so it
      // must start from the current value - not undefined, which would clear it on an unrelated save.
      ownerLoginUserId: this.site.ownerLoginUserId,
      // Seeded as the stored number, or empty for none, so a save always sends the number the form
      // shows: on the update input an empty one clears it, and an untouched one is resent as-is.
      phoneNumber: this.site.phoneNumber ?? '',
      address: { ...this.site.address },
      active: this.site.active,
    };
    this.validationError = '';
    this.editing = true;
    this.emitEditState();
  }

  cancelEdit() {
    if (this.isNew) {
      // Navigate back to sites list if cancelling a new site
      void router?.navigate(
        resolveRouterPath(`maintenance/companies/${this.groupId}/sites`)
      );
      return;
    }
    this.editing = false;
    this.editData = {};
    this.validationError = '';
    this.emitEditState();
  }

  /**
   * Returns the labels of any required fields that are missing. Unit and
   * Description are intentionally optional; everything else is mandatory.
   */
  private getMissingRequiredFields(): string[] {
    const d = this.editData;
    const a: Partial<Address> = d.address ?? {};
    const missing: string[] = [];
    if (!d.name?.trim()) missing.push(`${terms.loginSite.value} Name`);

    // The site's contact person is its owner, optional and validated server-side against group
    // membership; never a required field here. The site's own phone number is optional too, and
    // checked by the phone rule in saveEdit rather than here, since it is never missing, only wrong.

    // What a site must have is a location an auditor can be routed to - the pin - plus a readable
    // address that names it. The structured parts are best-effort: a landmark or business often
    // resolves with no street number or route at all.
    if (typeof a.latitude !== 'number' || typeof a.longitude !== 'number') {
      missing.push(`Location (pin the ${terms.loginSite.lower} on the map)`);
    }
    if (!this.hasReadableAddress(a)) missing.push('Address');
    return missing;
  }

  /**
   * A readable address can come from the place name, the structured components, or Google's
   * formatted string - matching Address.ToDisplayString on the server. A hand-entered address is
   * readable without Google ever having assembled a formatted string for it.
   */
  private hasReadableAddress(a: Partial<Address>): boolean {
    return Boolean(
      a.placeName?.trim() ||
      a.formattedAddress?.trim() ||
      a.streetName?.trim() ||
      a.suburb?.trim()
    );
  }

  /**
   * Saves the form, answering whether the save landed, so the workspace releases the site's lock
   * only then (a refused save leaves the edit open, under the lock, to be corrected).
   */
  async saveEdit(): Promise<boolean> {
    if (!this.store) return false;

    const missing = this.getMissingRequiredFields();
    if (missing.length > 0) {
      this.validationError = `Please complete all required fields: ${missing.join(', ')}.`;
      return false;
    }
    // The phone rule is applied before anything is sent (D-151 rule 3), in the server's own words,
    // and the number goes out as it will be stored: trimmed, or empty for none.
    const phone = normalizePhoneNumber(this.editData.phoneNumber);
    if (phone.error) {
      this.validationError = phone.error;
      return false;
    }
    this.editData = { ...this.editData, phoneNumber: phone.value ?? '' };
    this.validationError = '';

    // Capture isNew before the async call since store update will change it
    const wasNew = this.isNew;

    this.saving = true;
    this.emitEditState();
    const siteId = await this.store.saveSite(this.editData);
    this.saving = false;
    if (siteId) {
      this.editing = false;
      this.editData = {};
      // If this was a new site, navigate to the real site URL
      if (wasNew) {
        void router?.navigate(
          resolveRouterPath(
            `maintenance/companies/${this.groupId}/sites/${siteId}`
          )
        );
      }
    }
    // One emit however the save ended, so the bar's Save button never sticks on loading
    // after a failure.
    this.emitEditState();
    return siteId !== null;
  }

  private handleFieldChange(field: keyof LoginSite, value: unknown) {
    this.editData = { ...this.editData, [field]: value };
  }

  private handleAddressChange(e: CustomEvent<{ address: Address }>) {
    this.editData = { ...this.editData, address: e.detail.address };
  }

  // ── Standalone owner change (read-only mode) ──────────────────────────────

  /**
   * Opens the contact picker under the site's edit lock (@LOCK-045): the lock is taken first, so a
   * contact change cannot land in the middle of another person's edit, and a refusal names them
   * (RecordEditLock.take) and opens nothing.
   */
  private async openOwnerDialog() {
    this.ownerActionError = '';
    if (this.editLock) {
      if (!(await this.editLock.take())) return;
      this.contactLockHeld = true;
    }
    this.ownerDialogOpen = true;
  }

  /** Claims the release this overview owes for an open Change Contact, once. */
  private claimContactLock(): boolean {
    const held = this.contactLockHeld;
    this.contactLockHeld = false;
    return held;
  }

  private async onOwnerDialogSelect(e: CustomEvent<SearchDialogSelection>) {
    // The dialog only emits a selection; the host closes it and commits the change, then lets the
    // site's lock go whatever the answer. The release is claimed before anything is awaited, so the
    // dialog's own hide, which follows, does not release under the change.
    const held = this.claimContactLock();
    this.ownerDialogOpen = false;
    try {
      await this.commitOwner(e.detail.id);
    } finally {
      if (held) await this.editLock?.release();
    }
  }

  /** The picker closed: without a selection, the lock taken for it is let go. */
  private onOwnerDialogHide() {
    this.ownerDialogOpen = false;
    if (this.claimContactLock()) void this.editLock?.release();
  }

  /**
   * Commit an owner change through the standalone endpoint (store.setOwner). Used by the read-only
   * "Change Contact" flow only; the edit form instead carries the owner on its save. On failure the
   * error is surfaced inline rather than thrown, so a rejected owner change never loses the page.
   */
  private async commitOwner(ownerLoginUserId: string | null) {
    if (!this.store) return;
    this.ownerActionBusy = true;
    this.ownerActionError = '';
    const error = await this.store.setOwner(ownerLoginUserId);
    this.ownerActionBusy = false;
    if (error) this.ownerActionError = error;
  }

  private renderDetailsSection() {
    if (this.loading && !this.site) {
      return this.renderDetailsSkeleton();
    }

    if (this.editing) {
      return this.renderDetailsEdit();
    }

    return this.renderDetailsReadonly();
  }

  private renderDetailsSkeleton() {
    return html`
      <section class="card">
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="building"></sl-icon>
            ${terms.loginSite.value} Details
          </span>
        </div>
        <div class="info-grid">
          ${[1, 2, 3, 4].map(
            () => html`
              <div class="info-item">
                <sl-skeleton
                  effect="pulse"
                  style="width: 60%; height: 0.75rem;"
                ></sl-skeleton>
                <sl-skeleton
                  effect="pulse"
                  style="width: 80%; height: 1rem; margin-top: 0.25rem;"
                ></sl-skeleton>
              </div>
            `
          )}
        </div>
      </section>
    `;
  }

  private renderDetailsReadonly() {
    const site = this.site;
    if (!site) return nothing;

    return html`
      <section class="card">
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="building"></sl-icon>
            ${terms.loginSite.value} Details
          </span>
          <!-- Edit lives on the workspace action bar (2026-09-05, consistency with
               product and company). -->
        </div>

        <!--
          LEAN ON PURPOSE (architect, 2026-09-05, same ruling as the member overview): the
          header above owns the site's name and its operating status; this card says only
          what the header does not - owner, the site's phone (D-151) and address. Name returns in EDIT mode, where it
          is the work itself.
        -->
        <div class="readonly-details">
          <!-- Label:value rows, the product detail page's layout (architect, 2026-09-05) -
               same classes as the member overview so the two workspaces read as one design. -->
          <div class="detail-rows">
            <div class="detail-row">
              <span class="detail-label"
                >${terms.loginSite.value} ${terms.siteOwner.value}</span
              >
              <span class="detail-value ${this.ownerName ? '' : 'placeholder'}">
                ${
                  // Name, email and phone, the email and the phone as links (D-151 rule 4). A
                  // detail the owner does not have is left out, never shown as an empty label.
                  // The name is the person's, looked up by id with their card behind it (rule 8),
                  // not the name string the site's answer carries.
                  this.ownerName && site.ownerLoginUserId
                    ? html`<user-name
                          .userId=${site.ownerLoginUserId}
                        ></user-name
                        >${
                          this.ownerEmail
                            ? html` <a
                                class="owner-contact"
                                href="mailto:${this.ownerEmail}"
                                >${this.ownerEmail}</a
                              >`
                            : nothing
                        }${
                          this.ownerPhoneNumber
                            ? html` <a
                                class="owner-contact"
                                href=${phoneLinkTarget(this.ownerPhoneNumber)}
                                >${this.ownerPhoneNumber}</a
                              >`
                            : nothing
                        }`
                    : `No ${terms.siteOwner.lower} set`
                }
                ${
                  // Withdrawn while another person edits the site (@LOCK-040): the change would
                  // be refused, and the banner above names who is editing.
                  this.lockedByOther
                    ? nothing
                    : html`<sl-button
                        size="small"
                        variant="default"
                        class="owner-inline-action"
                        ?loading=${this.ownerActionBusy}
                        @click=${this.openOwnerDialog}
                      >
                        <sl-icon slot="prefix" name="person-gear"></sl-icon>
                        ${this.ownerName ? `Change ${terms.siteOwner.value}` : `Set ${terms.siteOwner.value}`}
                      </sl-button>`
                }
              </span>
            </div>
            ${
              // The site's own number, under its owner, as a link that calls it (D-151 rule 1).
              // A site without one shows no Phone line at all.
              site.phoneNumber
                ? html`
                    <div class="detail-row">
                      <span class="detail-label">Phone</span>
                      <span class="detail-value">
                        <a
                          class="site-phone"
                          href=${phoneLinkTarget(site.phoneNumber)}
                          >${site.phoneNumber}</a
                        >
                      </span>
                    </div>
                  `
                : nothing
            }
          </div>

          ${
            this.ownerActionError
              ? html`
                  <sl-alert
                    variant="danger"
                    open
                    closable
                    @sl-after-hide=${() => (this.ownerActionError = '')}
                  >
                    <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                    ${this.ownerActionError}
                  </sl-alert>
                `
              : nothing
          }
        </div>

        <!-- Standalone owner change: pick a member of the site's group; the selection commits
             immediately via the store (PUT {id}/owner), no full site edit required. -->
        <agwa-search-dialog
          tag=${MEMBER_USER_SEARCH_TAG}
          label="${terms.loginSite.value} ${terms.siteOwner.value}"
          .open=${this.ownerDialogOpen}
          .context=${{ [MEMBER_USER_SEARCH_CONTEXT.loginGroupId]: this.groupId }}
          @select=${this.onOwnerDialogSelect}
          @dialog-hide=${this.onOwnerDialogHide}
        ></agwa-search-dialog>

        <!-- Address joins the label:value rows (architect, 2026-09-05: the product page's
             label-to-the-left format everywhere - the old block still put the value UNDER an
             uppercase label). The map rides inside the value cell, under the address lines. -->
        <div class="detail-rows">
          ${
            site.description
              ? html`
                  <div class="detail-row">
                    <span class="detail-label">Description</span>
                    <span class="detail-value">${site.description}</span>
                  </div>
                `
              : nothing
          }
          <div class="detail-row">
            <span class="detail-label">Address</span>
            <div class="detail-value">
              <agwa-address
                .address=${site.address}
                .mapImageUrl=${this.mapImageUrl}
                show-icon
                empty-label="No address set"
              ></agwa-address>
            </div>
          </div>
        </div>
      </section>
    `;
  }

  private renderDetailsEdit() {
    return html`
      <section class="card">
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="building"></sl-icon>
            Edit ${terms.loginSite.value} Details
          </span>
        </div>

        <div class="edit-form">
          <div class="scalar-fields">
            <div class="edit-row">
              <sl-input
                label="${terms.loginSite.value} Name"
                required
                .value=${this.editData.name || ''}
                @sl-input=${(e: Event) =>
                  this.handleFieldChange(
                    'name',
                    (e.target as HTMLInputElement).value
                  )}
              ></sl-input>
            </div>

            <div class="owner-edit">
              <!-- Owner picker (single mode): scoped to the site's group's people who have not been
                   deleted, blocked ones included (D-174). The
                   selection rides the save DTO (final-state), so it is not committed until Save. -->
              <agwa-search-picker
                tag=${MEMBER_USER_SEARCH_TAG}
                label="${terms.loginSite.value} ${terms.siteOwner.value}"
                placeholder="Search ${terms.staffUser.lowerPlural} by name or username…"
                .value=${this.editData.ownerLoginUserId || ''}
                .context=${{
                  [MEMBER_USER_SEARCH_CONTEXT.loginGroupId]: this.groupId,
                }}
                @selection-changed=${(e: CustomEvent<SearchPickerSelection>) =>
                  this.handleFieldChange('ownerLoginUserId', e.detail.id)}
              ></agwa-search-picker>
              ${
                this.editData.ownerLoginUserId
                  ? html`
                      <sl-button
                        variant="text"
                        size="small"
                        @click=${() =>
                          this.handleFieldChange('ownerLoginUserId', undefined)}
                      >
                        <sl-icon slot="prefix" name="x-circle"></sl-icon>
                        Clear ${terms.siteOwner.value}
                      </sl-button>
                    `
                  : nothing
              }
            </div>

            <!-- The site's own phone number, optional (D-151 rule 1). The rule's refusal shows
                 under the field as it is typed, and again blocks the save. -->
            <sl-input
              label="Phone"
              type="tel"
              .value=${this.editData.phoneNumber || ''}
              help-text=${normalizePhoneNumber(this.editData.phoneNumber).error ?? ''}
              @sl-input=${(e: Event) =>
                this.handleFieldChange(
                  'phoneNumber',
                  (e.target as HTMLInputElement).value
                )}
            ></sl-input>

            <sl-textarea
              label="Description"
              rows="2"
              .value=${this.editData.description || ''}
              @sl-input=${(e: Event) =>
                this.handleFieldChange(
                  'description',
                  (e.target as HTMLTextAreaElement).value
                )}
            ></sl-textarea>

            <sl-switch
              ?checked=${this.editData.active}
              @sl-change=${(e: Event) =>
                this.handleFieldChange(
                  'active',
                  (e.target as HTMLInputElement).checked
                )}
            >
              Active
            </sl-switch>
          </div>

          <sl-divider></sl-divider>

          <span class="info-label">Address</span>
          <agwa-site-address-edit
            .address=${this.editData.address || null}
            .currentAddress=${this.site?.address ?? null}
            @agwa-address-change=${this.handleAddressChange}
          ></agwa-site-address-edit>

          ${
            this.validationError
              ? html`
                  <sl-alert variant="danger" open>
                    <sl-icon slot="icon" name="exclamation-triangle"></sl-icon>
                    ${this.validationError}
                  </sl-alert>
                `
              : nothing
          }

          <!-- Save / Cancel live on the workspace action bar (2026-09-05). -->
        </div>
      </section>
    `;
  }

  private renderOrganizationTypes() {
    if (this.loading && this.domains.length === 0) {
      return html`
        <section class="card">
          <div class="section-header">
            <span class="section-title">
              <sl-icon name="diagram-3"></sl-icon>
              ${terms.userGroupDomain.plural}
            </span>
          </div>
          <sl-skeleton
            effect="pulse"
            style="width: 30%; height: 1.5rem;"
          ></sl-skeleton>
        </section>
      `;
    }

    // THE SECTION IS NOW THE CONTROL, not a read-only echo of it. It listed the site's types as
    // tags and sent the reader to a separate tab to change one, which meant the answer to "what is
    // this site" and the way to change it lived on two screens. The list below shows every type
    // with weight on the ones held, and the checkbox is the registration itself.
    return html`
      <section class="card">
        <div class="section-header">
          <span class="section-title">
            <sl-icon name="diagram-3"></sl-icon>
            ${terms.userGroupDomain.plural}
          </span>
        </div>
        ${
          this.site?.id
            ? html`
                <site-operation-types-section
                  .siteId=${this.site.id}
                  .siteName=${this.site.name ?? ''}
                  @domains-updated=${() => void this.store?.refresh()}
                ></site-operation-types-section>
              `
            : html`<p class="info-value empty">
                Save this ${terms.loginSite.lower} before registering
                ${terms.userGroupDomain.lowerPlural}.
              </p>`
        }
      </section>
    `;
  }

  render() {
    return html`
      <div class="overview-grid">
        <div class="section-card">${this.renderDetailsSection()}</div>
        ${this.renderOrganizationTypes()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'site-overview': SiteOverview;
  }
}
